import { useState } from "react";
import { Button, Dialog, Field, InlineMessage, MultiSelect } from "@nexus/ui";
import type { DashboardRecord } from "@/lib/dashboard/types";
import { useUpdateDashboard } from "@/hooks/dashboard/useUpdateDashboard";
import { useGrantableAccounts } from "../useGrantableAccounts";

export function TemplateSharingFields({
  template,
  onSaved,
}: {
  template: DashboardRecord;
  onSaved?: () => void;
}) {
  const accounts = useGrantableAccounts(true, "template");
  const update = useUpdateDashboard();
  const [selected, setSelected] = useState(() =>
    (template.audience_grants ?? [])
      .filter((grant) => grant.grantee_type === "account")
      .map((grant) => grant.grantee_key),
  );
  const options = (accounts.data ?? []).map((account) => ({
    value: account.account_id,
    label: account.email,
  }));
  const listed = new Set(options.map((option) => option.value));
  for (const grant of template.audience_grants ?? []) {
    if (grant.grantee_type === "account" && !listed.has(grant.grantee_key))
      options.push({ value: grant.grantee_key, label: `${grant.grantee_key} (unavailable)` });
  }

  async function save() {
    if (update.isPending || template.protected) return;
    try {
      await update.mutateAsync({
        id: template.id,
        audience_grants: selected.map((grantee_key) => ({ grantee_type: "account", grantee_key })),
      });
      onSaved?.();
    } catch {
      return;
    }
  }

  return (
    <div className="space-y-4">
      <Field label="Shared with">
        <MultiSelect
          label="Shared with"
          searchable
          selectAll={false}
          emptyLabel="Only you"
          options={options}
          value={selected}
          onChange={setSelected}
          disabled={update.isPending || template.protected}
        />
      </Field>
      {accounts.isLoading && (
        <p role="status" className="text-sm text-muted">
          Loading accounts...
        </p>
      )}
      {accounts.isError && (
        <InlineMessage tone="error" title="Accounts could not be loaded">
          <Button size="sm" onClick={() => void accounts.refetch()}>
            Retry
          </Button>
        </InlineMessage>
      )}
      {update.isError && (
        <InlineMessage tone="error" title="Template access could not be saved">
          {update.error.message}
        </InlineMessage>
      )}
      {update.isSuccess && (
        <p role="status" className="text-sm text-muted">
          Access saved.
        </p>
      )}
      <Button
        variant="primary"
        disabled={update.isPending || template.protected}
        onClick={() => void save()}
      >
        {update.isPending ? "Saving..." : "Save access"}
      </Button>
    </div>
  );
}

export function TemplateSharingDialog({
  template,
  onClose,
}: {
  template: DashboardRecord;
  onClose: () => void;
}) {
  return (
    <Dialog open title={`Share ${template.name}`} onClose={onClose}>
      <TemplateSharingFields template={template} onSaved={onClose} />
    </Dialog>
  );
}
